import { TourProvider, useTour } from '@reactour/tour';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactElement, ReactNode, useEffect } from 'react';

interface GuidedTourProps {
  children: ReactNode;
  initialOpen?: boolean;
}

// Define step interface
interface TourStep {
  selector: string;
  content: string | ReactElement;
  path: string;
  click?: string; // Optional click selector
  position?: [number, number]; // Optional position for the tooltip
}

// Tour Steps
const steps: TourStep[] = [
  {
    selector: '.guided-tour-exposure-score',
    content: (
      <>
        Your overall Exposure Score indicates your current security risk level.
        This score is calculated by analyzing the relationship between your
        threat profile, attack surface, and defense surface coverage.
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Platform%20Overview/CISO%20Dashboard/#exposure-score"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about exposure score
        </a>
      </>
    ),
    path: '/ciso'
  },
  {
    selector: '.guided-tour-score-trend',
    content: (
      <>
        Track your security posture improvements over time with the Score Trend
        graph. This visualization shows the relationship between your overall
        exposure, threat exposure, defense surface, and attack surface metrics.
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Platform%20Overview/CISO%20Dashboard/#score-trend"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about score trend
        </a>
      </>
    ),
    path: '/ciso'
  },
  {
    selector: '.guided-tour-activity-feed',
    content: (
      <>
        The Interpres Activity Feed provides users with a near real-time stream
        of updates pertaining to alterations in security products within their
        environment, alongside newly introduced content from Interpres or other
        reputable sources (e.g., MITRE ATT&CK™).
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Platform%20Overview/CISO%20Dashboard/#activity-feed"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about the activity feed
        </a>
      </>
    ),
    path: '/ciso'
  },
  {
    selector: '.tour-active-threat-profile-step',
    content: (
      <>
        This is your active threat profile. The first step to setup Interpres is
        to create your organization&#39;s threat profile. This information is
        used by Interpres to identify the most relevant threats and techniques
        for your organization. By default Interpres uses a generic profile. Feel
        free to complete the tour and go back to this step later.
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Initial%20Setup/Creating%20a%20Threat%20Profile/"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more creating a threat profiles
        </a>
      </>
    ),
    path: '/threat-profiles'
  },
  {
    selector: '.tour-step-add-integration',
    content: (
      <>
        The next step is to add at a least one integration. This will allow
        Interpres to collect data from your security tools and provide you with
        a comprehensive view of your security posture. It is recommended to add
        a vulnerability scanner, endpoint security product, and SIEM. However,
        even a single integration is enough to perform an assessment.
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Initial%20Setup/Connecting%20Integrations/"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about adding an integration
        </a>
      </>
    ),
    path: '/integrations'
  },
  {
    selector: '.tour-step-table-first-item',
    content: (
      <>
        Once a threat profile is created and integration(s) are added, Interpres
        will start prioritizing the techniques that are most relevant to your
        organization. This is done by analyzing the techniques used by threat
        groups that are targeting organizations likes yours. Furthermore,
        Interpres will identify your telemetry and detection coverage for each
        technique.
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Platform%20Overview/Threat%20Exposure/#techniques"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about adversary techniques
        </a>
      </>
    ),
    path: '/techniques'
  },
  {
    selector: '.tour-step-table-first-item',
    content: (
      <>
        Interpres regularly updates its threat intelligence database. Allowing
        your organization to stay up to date with the latest threats and quickly
        help you determine your organizations exposure to recent campaigns.
        <br />
        <br />
        <a
          href="https://docs.interpres.io/Platform%20Overview/Threat%20Exposure/#campaigns"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about campaigns
        </a>
      </>
    ),
    path: '/campaigns'
  },
  {
    selector: '.guided-tour-button',
    content: (
      <>
        This concludes the guided tour. You can always return to the tour by
        selecting <b>Guided Tour</b> under the <b>Help</b> menu in the bottom
        left.
        <br />
        <br />
        <a
          href="https://docs.interpres.io"
          target="_blank"
          style={{ color: '#32f0c0' }}
        >
          Learn more about the Interpres Product
        </a>
      </>
    ),
    path: '/ciso'
  }
];

// Tour Steps
const setupSteps: TourStep[] = [
  {
    selector: '.welcome-step',
    content:
      "Welcome to Interpres, your comprehensive cybersecurity platform that provides a holistic view of your organization's security posture across threat exposure, attack surface, and defense capabilities.",
    path: '/ciso',
    position: [20, 20] as [number, number]
  },
  {
    selector: '.tour-create-threat-profile-step',
    click: '.tour-create-threat-profile-button',
    content: (
      <>
        To begin, let&#39;s set up your organization&#39;s unique threat
        profile. Complete your profile by entering a name, description, and
        selecting your industry, organization size, and countries of operation.
        Select the platforms relevant to your environment. These selections help
        determine which threats and techniques are most relevant to your
        organization.
      </>
    ),
    path: '/threat-profiles'
  },
  {
    selector: '.tour-active-threat-profile-step',
    click: '.tour-create-threat-profile-button',
    content: (
      <>
        To begin, let&#39;s set up your organization&#39;s unique threat
        profile. Click on the Threat Exposure menu, then select Threat Profile.
      </>
    ),
    path: '/threat-profiles'
  }
  // ...
];

const GuidedTour = ({ children, initialOpen = false }: GuidedTourProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Define colors matching your site
  const colors = {
    primary: '#8051ff', // Purple from activity feed dots
    secondary: '#ef5a3d', //  Red/orange from bottom chart line
    accent: '#32f0c0', // Mint green from top chart line
    background: '#1a1a1a', // Dark background
    text: '#ffffff', // White text
    mutedText: '#a0a0a0' // Gray text for timestamps
  };

  // Border radius value used throughout the UI
  const radius = 8;

  // Check for element existence before showing tour
  const TourController = () => {
    const { isOpen, currentStep, setIsOpen } = useTour();

    // Watch for step changes and navigate if needed
    useEffect(() => {
      if (isOpen && steps[currentStep]) {
        const stepPath = steps[currentStep].path;
        const targetSelector = steps[currentStep].selector;
        const click = steps[currentStep]?.click || false;

        if (stepPath !== location.pathname) {
          // Temporarily close the tour during navigation
          setIsOpen(false);

          // Navigate to the correct path
          navigate(stepPath);

          // Set up a check for the element with a maximum wait time
          let attempts = 0;
          const maxAttempts = 10; // Maximum 10 attempts (5 seconds total)

          const checkElement = () => {
            attempts++;
            const elementExists = document.querySelector(targetSelector);
            let clickExists = null;
            if (click) {
              clickExists = document.querySelector(click);
            }

            console.log(
              `Checking for element: ${targetSelector}, Attempt: ${attempts}`
            );
            console.log(`Element exists: ${elementExists}`);
            console.log(`Click exists: ${clickExists}`);

            if (elementExists) {
              setIsOpen(true);
            } else if (clickExists) {
              (clickExists as HTMLElement).click();
              // Element not found yet, try again in 2000ms
              setTimeout(checkElement, 800);
            } else if (attempts < maxAttempts) {
              // Element not found yet, try again in 1000ms
              setTimeout(checkElement, 600);
            } else {
              // Max attempts reached, show tour anyway
              console.warn(
                `Tour element "${targetSelector}" not found after ${maxAttempts} attempts`
              );
              setIsOpen(true);
            }
          };

          // Start checking after a small initial delay
          setTimeout(checkElement, 800);
        }
      }
    }, [currentStep, isOpen, navigate, setIsOpen]);

    return null;
  };

  return (
    <TourProvider
      steps={steps}
      styles={{
        popover: base => ({
          ...base,
          '--reactour-accent': colors.accent,
          backgroundColor: colors.background,
          color: colors.text,
          borderRadius: radius,
          border: `1px solid ${colors.primary}`,
          boxShadow: `0 0 10px rgba(35, 4, 121, 0.5)`
        }),
        maskArea: base => ({
          ...base,
          rx: radius,
          color: colors.accent
        }),
        maskWrapper: base => ({
          ...base,
          color: 'rgba(31, 41, 55, 0.8)'
        }),
        badge: base => ({
          ...base,
          left: 'auto',
          right: '-0.8125em',
          backgroundColor: colors.primary
        }),
        controls: base => ({
          ...base,
          marginTop: 20,
          button: {
            backgroundColor: colors.primary
          }
        }),
        close: base => ({
          ...base,
          right: 'auto',
          left: 8,
          top: 8,
          color: colors.mutedText
        }),
        arrow: base => ({
          ...base,
          color: colors.primary
        }),
        dot: (base, { current }) => ({
          ...base,
          backgroundColor: current ? colors.accent : colors.mutedText,
          border: current ? `2px solid ${colors.primary}` : 'none'
        })
      }}
    >
      <TourController />
      {children}
    </TourProvider>
  );
};

// Simple hook to control the tour from anywhere
export const useTourNavigation = () => {
  const { setIsOpen, setCurrentStep, isOpen } = useTour();
  const navigate = useNavigate();

  // Start tour on a specific page
  const startTourAtPath = path => {
    const stepIndex = steps.findIndex(step => step.path === path);
    if (stepIndex >= 0) {
      navigate(path);
      setCurrentStep(stepIndex);
      setIsOpen(true);
    }
  };

  return { startTourAtPath, isOpen, setIsOpen };
};

export default GuidedTour;
